/**
 * YARD CONTROL — which row of the yard every drum is standing in (0152).
 *
 * Until this screen the yard was one undivided fact: "in the plant". A cylinder found
 * leaking was held, and blocked from every action CTS can refuse — and then stood exactly
 * where it had been standing, in the working rows, with nothing but a database row saying
 * it was dangerous. A hold is not a chain across a gate.
 *
 * ACTION BEFORE INFORMATION. The screen opens on the areas with what is standing in each
 * and the move beside it, because moving a drum is what a yard hand came here to do. The
 * correction queue comes next, and the movement book last.
 *
 * THE QUEUE IS NOT A BUG LIST. A cylinder whose area nobody ever recorded reads "area not
 * recorded" and waits for a person to say where it is. Nothing is backfilled and nothing
 * is inferred: the suggestion beside each row is a starting point for the operator, and
 * it is never applied by anything.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session, type Views } from '../api.js';
import { useAsk, useRun, go, useRouteQuery } from '../hooks.js';
import { DamageQueue, QuarantineQueue, DamagedCylinders } from './casework.js';
import { CustomerPickerById } from '../pickers.js';
import { Banner, Button, Card, EmptyState, Kpi, Loading, Pill, Register, Select, Serial, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { FleetTabs } from '../fleet.js';
import { count, date, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('yard')!.messages;

type Area = Views['yard.areas']['result'][number];
type Branch = Views['yard.branches']['result'][number];
type Standing = Views['yard.standing']['result']['rows'][number];
type Unrecorded = Views['yard.unrecorded']['result']['rows'][number];

const KINDS = ['receiving', 'quarantine', 'preparation', 'filling', 'filled_stock', 'dispatch_staging'] as const;
const KIND_LABEL: Record<string, string> = {
  receiving: 'Receiving', quarantine: 'Quarantine / isolation', preparation: 'Empty / preparation',
  filling: 'Filling', filled_stock: 'Filled stock', dispatch_staging: 'Dispatch staging',
};
/** The order the work actually happens in, so the yard reads down the page as the day runs. */
const KIND_ORDER: Record<string, number> = {
  receiving: 0, quarantine: 1, preparation: 2, filling: 3, filled_stock: 4, dispatch_staging: 5,
};

type StageRow = Views['yard.stages']['result']['rows']['rows'][number];
type StageKey = Views['yard.stages']['result']['stage'];
/** The day's stages, in the order the work happens. Each says what to do next, and where. */
const STAGE_WORDS: Record<StageKey, { label: string; note: string; tone: 'empty' | 'primary' | 'good' | 'danger' | 'money' | 'muted'; next: string; href: (r: StageRow) => string }> = {
  to_inspect: { label: 'To inspect', note: 'came back, nobody looked yet', tone: 'money', next: 'Inspect', href: () => '#/filling?tab=inspect' },
  empty:      { label: 'Empty, fit to fill', note: 'inspected, nothing blocking', tone: 'empty', next: 'Fill', href: () => '#/filling' },
  to_check:   { label: 'Filled, to check', note: 'gas not yet checked', tone: 'primary', next: 'Check', href: () => '#/filling?tab=check' },
  ready:      { label: 'Ready to send', note: 'full, checked, fit', tone: 'good', next: 'Send out', href: () => '#/move?dir=out' },
  quarantine: { label: 'Quarantined', note: 'held — not to be filled or sent', tone: 'danger', next: 'Settle', href: (r) => `#/casework?queue=quarantine&cylinder=${r.cylinderId}` },
  test_due:   { label: 'Test overdue', note: 'must not be filled', tone: 'danger', next: 'Record test', href: (r) => `#/cylinders/${r.cylinderId}` },
  repair:     { label: 'In repair', note: 'away for repair', tone: 'muted', next: 'Passport', href: (r) => `#/cylinders/${r.cylinderId}` },
  testing:    { label: 'In testing', note: 'away for periodic test', tone: 'muted', next: 'Passport', href: (r) => `#/cylinders/${r.cylinderId}` },
};

type Open =
  | { what: 'open-area' }
  | { what: 'close-area'; area: Area }
  | { what: 'nominate'; area: Area }
  | { what: 'move'; cylinderId: number; label: string; suggest: string | null;
      /** the yard the drum is standing in — the only one whose rows it may be moved between */
      branchId: number | null }
  | null;

export function Yard({ session }: { session: Session }) {
  const [open, setOpen] = useState<Open>(null);
  // WHICH ROW IS BEING LOOKED INTO. One at a time: a yard hand is standing at one row, and
  // six open lists is six lists nobody reads.
  const [looking, setLooking] = useState<Area | null>(null);
  const [page, setPage] = useState(1);
  const [movePage, setMovePage] = useState(1);

  const yards = useAsk(() => api.view('yard.branches', {}), [], MESSAGES);
  const areas = useAsk(() => api.view('yard.areas', {}), [], MESSAGES);
  const queue = useAsk(() => api.view('yard.unrecorded', { page, pageSize: 25 }), [page], MESSAGES);
  const book = useAsk(() => api.view('yard.movements', { page: movePage, pageSize: 25 }), [movePage], MESSAGES);

  const mayArrange = can(session, 'yard.manage_areas');
  const mayMove = can(session, 'yard.move_cylinder');

  function done() { setOpen(null); yards.reload(); areas.reload(); queue.reload(); book.reload(); }

  // every hook ABOVE the early returns below — a hook after a conditional return crashes the
  // page on its second render, which is exactly what the walk found on 11 Oct
  const tabRaw = useRouteQuery('tab');
  const tab: 'standing' | 'damage' | 'quarantine' | 'rows' = tabRaw === 'damage' || tabRaw === 'quarantine' || tabRaw === 'rows' ? tabRaw : 'standing';
  const [stage, setStage] = useState<StageKey | undefined>(undefined);
  const [stagePage, setStagePage] = useState(1);
  const stagesAsked = useAsk(() => api.view('yard.stages', { ...(stage ? { stage } : {}), page: stagePage, pageSize: 25 }), [stage, stagePage], MESSAGES);

  const sub = 'Everything standing here by the stage it has reached, the damage and quarantine cases to settle, and — if the yard wants it — its rows.';
  if (areas.loading) return <Page title="Yard" sub={sub}><Loading what="the yard" /></Page>;
  if (areas.error) {
    return <Page title="Yard" sub={sub}>
      <FleetTabs active="yard" session={session} />
      <Banner kind="error" title={areas.error.title} {...(areas.error.detail ? { detail: areas.error.detail } : {})} />
    </Page>;
  }
  const all = areas.data!;
  const live = [...all].filter((a) => a.active)
    .sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9) || a.code.localeCompare(b.code));
  const closed = all.filter((a) => !a.active);
  // READ FROM THE YARDS, NOT FROM THE AREAS. Deriving the list of yards from the areas on
  // the page can only ever name yards that already have one, so the yard with no areas —
  // the one case where the warning would matter most — could never appear in it.
  const yardList = yards.data ?? [];
  const isolationless = yardList.filter((y) => y.areas > 0 && !y.hasIsolationArea).map((y) => y.code);

  const st = stagesAsked.data;
  const shown: StageKey = stage ?? st?.stage ?? 'empty';
  const w = STAGE_WORDS[shown];
  const mayDamage = can(session, 'view.damage');
  const held = st?.counts.quarantine ?? 0;

  return (
    <Page title="Yard" sub={sub}>
      {open ? <YardForm open={open} areas={live} yards={yardList} onDone={done} onCancel={() => setOpen(null)} /> : null}

      {/* 11 Oct — the owner: "yard, damage and quarantine should be one page". They are. */}
      <div className="seg" role="tablist" aria-label="The yard" style={{ marginBottom: 12 }}>
        <button type="button" className={`seg-btn${tab === 'standing' ? ' on' : ''}`} onClick={() => go('#/yard')}>Standing here{st ? ` (${count(st.total)})` : ''}</button>
        {mayDamage ? <button type="button" className={`seg-btn${tab === 'damage' ? ' on' : ''}`} onClick={() => go('#/yard?tab=damage')}>Damaged</button> : null}
        {mayDamage ? <button type="button" className={`seg-btn${tab === 'quarantine' ? ' on' : ''}`} onClick={() => go('#/yard?tab=quarantine')}>Quarantined{held ? ` (${count(held)})` : ''}</button> : null}
        <button type="button" className={`seg-btn${tab === 'rows' ? ' on' : ''}`} onClick={() => go('#/yard?tab=rows')}>Rows{live.length ? ` (${count(live.length)})` : ''}</button>
      </div>

      {tab === 'damage' ? <><DamagedCylinders session={session} base="/yard?tab=damage" /><DamageQueue session={session} base="/yard?tab=damage" /></> : null}
      {tab === 'quarantine' ? <QuarantineQueue session={session} /> : null}
      {tab === 'standing' ? <HowACylinderGetsHere session={session} yards={yardList} /> : null}
      {tab === 'standing' ? <>

      {/* 11 Oct — THE YARD WITHOUT SETUP (the owner: "a mess, confusing"; the brief §1.2: the
          system determines locations). One tile per stage of the day; the list under them is
          the stage chosen, each row with its next step. */}
      <div className="kpis" data-stages>
        {(Object.keys(STAGE_WORDS) as StageKey[]).map((k) => {
          const v = st?.counts[k] ?? 0;
          if (v === 0 && !['to_inspect', 'empty', 'to_check', 'ready'].includes(k)) return null;
          return (
            <a key={k} className={`kpi-link${shown === k ? ' on' : ''}`} href={`#/yard?stage=${k}`}
               onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); setStage(k); setStagePage(1); }}>
              <Kpi label={STAGE_WORDS[k].label} value={count(v)} note={STAGE_WORDS[k].note} tone={STAGE_WORDS[k].tone} />
            </a>
          );
        })}
      </div>
      <Card title={w.label} sub={`${w.note} · ${count(st?.counts[shown] ?? 0)} cylinder${(st?.counts[shown] ?? 0) === 1 ? '' : 's'}`}
            actions={<Button kind="primary" onClick={() => go(w.href({ cylinderId: 0 } as StageRow))}>{w.next}</Button>}>
        {stagesAsked.loading && !st ? <Loading what="the yard" />
          : stagesAsked.error ? <Banner kind="error" title={stagesAsked.error.title} {...(stagesAsked.error.detail ? { detail: stagesAsked.error.detail } : {})} />
          : (<>
              <Register<StageRow> rows={st!.rows.rows} empty={st!.total === 0 ? 'No cylinder is standing in the yard.' : `Nothing is ${w.label.toLowerCase()} right now.`}
                columns={[
                  { key: 'cyl', head: 'Cylinder', cell: (r) => <><Serial>{r.internalId}</Serial><div className="muted">{r.serialNo} · {words(r.gasService)}</div></> },
                  { key: 'owner', head: 'Whose', cell: (r) => r.ownerKind === 'company' ? 'Ours' : (r.ownerName ?? 'Customer\'s') },
                  { key: 'since', head: 'Here since', cell: (r) => date(r.since) },
                  { key: 'where', head: 'Row', cell: (r) => r.areaCode ?? <span className="muted">—</span> },
                  { key: 'why', head: 'Standing', cell: (r) => shown === 'to_inspect' ? words(r.finding ?? 'not_recorded') : shown === 'test_due' ? words(r.testStatus ?? '') : <Pill tone={w.tone === 'muted' ? 'empty' : w.tone}>{w.label}</Pill> },
                  { key: 'next', head: '', cell: (r) => <a href={w.href(r)} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(w.href(r)); }}>{w.next} →</a> },
                ]}
 />
              <Pager page={st!.rows.page} onPage={setStagePage} />
            </>)}
      </Card>

      </> : null}

      {tab === 'rows' ? <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '18px 0 8px' }}>
        <h2 style={{ fontSize: 15, margin: 0 }}>Rows of the yard <span className="muted" style={{ fontWeight: 400 }}>— optional. Which row of the yard every drum is standing in, for yards that want it written down.</span></h2>
        {mayArrange ? <Button kind="primary" onClick={() => setOpen({ what: 'open-area' })}>Open an area</Button> : null}
      </div>

      {/* THE ONE THING ON THIS SCREEN THAT IS A SAFETY PROBLEM RIGHT NOW. A yard that has
          adopted areas and nominated no isolation area has nowhere recorded to put the next
          leaking cylinder. The nightly self-check raises it too; it is said here because
          this is the screen where somebody can fix it. */}
      {isolationless.length > 0 ? (
        <Banner kind="error" title={`${isolationless.join(', ')} ${isolationless.length === 1 ? 'has' : 'have'} no isolation area`}
                detail={'A cylinder found leaking is held and blocked from every action — but nothing records which row of the yard '
                        + 'it has been pulled out to. Open a quarantine area and nominate it.'} />
      ) : null}

      {live.length === 0 ? (
        <p className="muted">This yard has no rows written down, and needs none: the stages under "Standing here" come from the work itself. Open rows only if the yard wants a drum's physical row recorded too.</p>
      ) : (
        <Card title="The yard, in the order the work happens" sub="How many drums are standing in each row, and how many of those are held.">
          <div data-areas>
            <Register columns={areaColumns(mayArrange, looking, setLooking, setOpen)} rows={live} empty="" />
          </div>
          {looking ? <InArea area={looking} mayMove={mayMove} onMove={setOpen} /> : null}
        </Card>
      )}

      {/* THE CORRECTION QUEUE (owner's constraint). Never backfilled by assumption. Shown only once the yard has rows. */}
      {live.length > 0 ? <Card title="Area not recorded"
            sub="Cylinders standing in a yard that has areas, whose own area nobody has ever recorded. Somebody has to look.">
        {queue.loading ? <Loading what="the queue" />
          : queue.error ? <Banner kind="error" title={queue.error.title} {...(queue.error.detail ? { detail: queue.error.detail } : {})} />
          : (<>
              <div data-queue>
                <Register columns={queueColumns(mayMove, setOpen)} rows={queue.data!.rows}
                          empty="Every cylinder in this yard has its area recorded."
 />
              </div>
              <Pager page={queue.data!.page} onPage={setPage} />
            </>)}
      </Card> : null}

      {live.length > 0 ? <Card title="Movement book" sub="Every move between the rows of a yard, who made it and why. Nothing here is edited.">
        {book.loading ? <Loading what="the movement book" />
          : book.error ? <Banner kind="error" title={book.error.title} {...(book.error.detail ? { detail: book.error.detail } : {})} />
          : (<>
              <Register columns={BOOK_COLUMNS} rows={book.data!.rows}
                        empty="No cylinder has been moved between areas yet."
 />
              <Pager page={book.data!.page} onPage={setMovePage} />
            </>)}
      </Card> : null}

      {closed.length > 0 ? (
        <Card title="Closed areas" sub="Kept because the movements that named them still name them.">
          <Register rows={closed} empty="" columns={[
            { key: 'code', head: 'Area', cell: (a: Area) => <>{a.code}<div className="muted">{a.branchCode}</div></> },
            { key: 'kind', head: 'For', cell: (a: Area) => KIND_LABEL[a.kind] ?? a.kind },
            { key: 'closed', head: 'Closed', cell: (a: Area) => date(a.closedOn) },
          ]} />
        </Card>
      ) : null}
      </> : null}
    </Page>
  );
}

/**
 * HOW A CYLINDER GETS HERE (the owner, 11 Oct: "how does one take a cylinder from a customer
 * and assign which yard or area?"). The answer, on the page: ours come back through Receive
 * in; a customer's own cylinder is received against that customer; the yard is the one the
 * session is working at (the picker in the rail switches it); the stage is read off the work,
 * and a physical row is optional.
 */
function HowACylinderGetsHere({ session, yards }: { session: Session; yards: readonly Branch[] }) {
  const [customerId, setCustomerId] = useState<string>('');
  const here = session.branches.find((b) => b.branchId === session.activeBranchId) ?? session.branches.find((b) => b.isDefault) ?? null;
  const yardName = here ? `${here.name} (${here.code})` : yards[0] ? `${yards[0].name} (${yards[0].code})` : 'this yard';
  return (
    <Card title="Bringing a cylinder in" sub={`Everything received lands at ${yardName} — the yard you are signed in at${session.branches.length > 1 ? '; the picker in the sidebar switches it' : ''}. Its stage is worked out from what happens to it; a physical row is optional.`}>
      <div className="grid three" style={{ alignItems: 'end' }}>
        <div>
          <strong>Ours, coming back from a customer</strong>
          <p className="muted" style={{ margin: '4px 0 8px' }}>Scan it on Receive in, record its condition. It lands under "To inspect" or "Empty".</p>
          {can(session, 'challan.create') ? <Button kind="primary" onClick={() => go('#/move?dir=in')}>Receive in</Button> : null}
        </div>
        <div>
          <strong>A customer's own cylinder, for filling</strong>
          <p className="muted" style={{ margin: '4px 0 8px' }}>Received against the customer who owns it; it is never counted in our fleet.</p>
          {can(session, 'job.receive') || can(session, 'job.create') ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <CustomerPickerById name="receive-for" label="Whose cylinder?" customerId={customerId} onPick={(v) => setCustomerId(v)} placeholder="Search the customer…" />
              </div>
              <Button kind="primary" disabled={!customerId} onClick={() => go(`#/customers/${customerId}?tab=jobs`)}>Receive theirs</Button>
            </div>
          ) : null}
        </div>
        <div>
          <strong>Already here, but wrong or unknown</strong>
          <p className="muted" style={{ margin: '4px 0 8px' }}>A cylinder in the wrong state or yard is corrected from its passport, with the reason kept.</p>
          <Button onClick={() => go('#/find')}>Find a cylinder</Button>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ the registers */

function areaColumns(mayArrange: boolean, looking: Area | null, setLooking: (a: Area | null) => void, setOpen: (o: Open) => void): readonly Column<Area>[] {
  return [
    { key: 'area', head: 'Area', cell: (a) => (
        <><Serial>{a.code}</Serial>
          <div className="muted">{a.name}{a.branchCode ? ` · ${a.branchCode}` : ''}</div></>) },
    { key: 'kind', head: 'For', cell: (a) => (
        <>{KIND_LABEL[a.kind] ?? a.kind}
          {a.isDefaultQuarantine ? <> <Pill tone="danger">isolation</Pill></> : null}</>) },
    { key: 'standing', head: 'Standing here', cell: (a) => (
        <>{count(a.cylinders)}
          {a.held > 0 ? <div className="muted">{count(a.held)} held</div> : null}</>) },
    { key: 'do', head: '', cell: (a) => (
        <>{a.cylinders > 0
            ? <Button small onClick={() => setLooking(looking && looking.areaId === a.areaId ? null : a)}>
                {looking && looking.areaId === a.areaId ? 'Hide' : 'What is in it'}
              </Button>
            : null}
          {' '}{mayArrange && a.kind === 'quarantine' && !a.isDefaultQuarantine
            ? <Button small onClick={() => setOpen({ what: 'nominate', area: a })}>Make it the isolation area</Button> : null}
          {' '}{mayArrange && !a.isDefaultQuarantine && a.cylinders === 0
            ? <Button small onClick={() => setOpen({ what: 'close-area', area: a })}>Close</Button> : null}</>) },
  ];
}

function queueColumns(mayMove: boolean, setOpen: (o: Open) => void): readonly Column<Unrecorded>[] {
  return [
    { key: 'cyl', head: 'Cylinder', cell: (r) => (
        <><a href={`#/cylinders?open=${r.cylinderId}`}><Serial>{r.internalId}</Serial></a>
          {r.serialNo ? <div className="muted">stamped {r.serialNo}</div> : null}</>) },
    { key: 'where', head: 'Yard', cell: (r) => r.branchCode },
    { key: 'what', head: 'What the register says', cell: (r) => (
        <>{r.state}
          {r.held ? <> <Pill tone="danger">held</Pill></> : null}
          {r.safetyRelease === 'quarantined' ? <> <Pill tone="danger">quarantined</Pill></> : null}
          {r.blocked.toFill.length > 0
            ? <div className="muted">{r.blocked.toFill.join('; ')}</div> : null}</>) },
    // A SUGGESTION, AND LABELLED ONE. A held drum suggests the isolation row precisely
    // because that is where somebody should go and check it actually is.
    { key: 'guess', head: 'Probably in', cell: (r) => (
        <span className="muted">{KIND_LABEL[r.suggestedKind] ?? r.suggestedKind} — check</span>) },
    { key: 'do', head: '', cell: (r) => mayMove
        ? <Button small kind="primary"
                  onClick={() => setOpen({ what: 'move', cylinderId: r.cylinderId,
                                           label: r.internalId ?? String(r.cylinderId), suggest: r.suggestedKind,
                                           branchId: r.branchId })}>
            Record where it is
          </Button>
        : null },
  ];
}

const BOOK_COLUMNS: readonly Column<Views['yard.movements']['result']['rows'][number]>[] = [
  { key: 'when', head: 'Moved', cell: (r) => <>{date(r.movedOn)}<div className="muted">{r.movedBy ?? '—'}</div></> },
  { key: 'cyl', head: 'Cylinder', cell: (r) => (
      <><a href={`#/cylinders?open=${r.cylinderId}`}><Serial>{r.internalId}</Serial></a>
        {r.serialNo ? <div className="muted">stamped {r.serialNo}</div> : null}</>) },
  { key: 'from', head: 'From', cell: (r) => r.fromArea ?? <span className="muted">not recorded</span> },
  { key: 'to', head: 'To', cell: (r) => <>{r.toArea}<div className="muted">{KIND_LABEL[r.toAreaKind] ?? r.toAreaKind}</div></> },
  { key: 'why', head: 'Why', cell: (r) => r.reason },
];

/* ------------------------------------------------------------------ what is in one row */

/**
 * Without this the screen could place a cylinder and never move it again: the correction
 * queue was the only list offering a move, and a drum leaves that queue the moment its area
 * is recorded. Every row answers the owner's six questions here — where it is, what state it
 * is in, what blocks it, and the next permitted action.
 */
function InArea({ area, mayMove, onMove }: { area: Area; mayMove: boolean; onMove: (o: Open) => void }) {
  const [page, setPage] = useState(1);
  const inside = useAsk(() => api.view('yard.standing', { areaId: area.areaId, page, pageSize: 25 }),
                        [area.areaId, page], MESSAGES);
  const columns: readonly Column<Standing>[] = [
    { key: 'cyl', head: 'Cylinder', cell: (r) => (
        <><a href={`#/cylinders?open=${r.cylinderId}`}><Serial>{r.internalId}</Serial></a>
          {r.serialNo ? <div className="muted">stamped {r.serialNo}</div> : null}</>) },
    { key: 'state', head: 'What it is', cell: (r) => (
        <>{r.state}{r.contents ? <div className="muted">{r.contents}</div> : null}</>) },
    // THE COMPLETE DECISION, not the hold. An open hold is one of the three authorities; the
    // recorded safety decision and Book 3's test status each stop a vessel on their own, and
    // a screen that said "nothing blocks it" about a condemned drum would be contradicting
    // the rules the commands enforce.
    { key: 'blocked', head: 'What blocks it', cell: (r) => (
        r.blocked.toFill.length === 0 && r.blocked.toSend.length === 0
          ? <span className="muted">nothing</span>
          : <>
              {r.held ? <Pill tone="danger">held</Pill> : null}
              {r.blocked.toFill.length > 0
                ? <div>not fillable: {r.blocked.toFill.join('; ')}</div> : null}
              {r.blocked.toSend.length > 0
                ? <div>not sendable: {r.blocked.toSend.join('; ')}</div> : null}
              {r.holdReason ? <div className="muted">{r.holdReason}</div> : null}
            </>) },
    { key: 'do', head: '', cell: (r) => mayMove
        ? <Button small onClick={() => onMove({ what: 'move', cylinderId: r.cylinderId,
                                                label: r.internalId ?? String(r.cylinderId), suggest: null,
                                                branchId: area.branchId })}>
            Move it
          </Button>
        : null },
  ];
  return (
    <Card title={`Standing in ${area.code}`} sub={`${KIND_LABEL[area.kind] ?? area.kind}${area.isDefaultQuarantine ? ' — this yard\u2019s isolation bay' : ''}.`}>
      {inside.loading ? <Loading what={`what is in ${area.code}`} />
        : inside.error ? <Banner kind="error" title={inside.error.title} {...(inside.error.detail ? { detail: inside.error.detail } : {})} />
        : (<>
            <div data-standing>
              <Register columns={columns} rows={inside.data!.rows} empty="Nothing is recorded as standing here." />
            </div>
            <Pager page={inside.data!.page} onPage={setPage} />
          </>)}
    </Card>
  );
}

/* ------------------------------------------------------------------ the forms */

function YardForm({ open, areas, yards, onDone, onCancel }: {
  open: NonNullable<Open>; areas: readonly Area[]; yards: readonly Branch[]; onDone: () => void; onCancel: () => void;
}) {
  const runner = useRun(MESSAGES);
  const [code, setCode] = useState('');
  const [kind, setKind] = useState<string>('receiving');
  const [name, setName] = useState('');
  const [isolation, setIsolation] = useState(false);
  const [reason, setReason] = useState('');
  const [on, setOn] = useState(today());
  // The move form starts on the area the register SUGGESTS, so the common case is one
  // click — and the operator can still say somewhere else, because the suggestion is a
  // guess about a physical yard and they are standing in it.
  // ONLY THE ROWS OF THE DRUM'S OWN YARD. A cylinder moves between the rows of the yard it is
  // standing in; moving it to another branch's row is a BRANCH TRANSFER, which is a different
  // act with its own paperwork. The door refuses it either way — this is so the operator is
  // never offered it.
  const movable = open.what === 'move' && open.branchId !== null
    ? areas.filter((a) => a.branchId === open.branchId) : areas;
  const suggested = open.what === 'move' && open.suggest
    ? movable.find((a) => a.kind === open.suggest) ?? null : null;
  const [areaId, setAreaId] = useState<string>(suggested ? String(suggested.areaId) : '');
  const [branchId, setBranchId] = useState<string>(yards.length === 1 ? String(yards[0]!.branchId) : '');
  const [needYard, setNeedYard] = useState(false);

  const branchOf = open.what === 'close-area' || open.what === 'nominate' ? open.area.branchCode : null;

  async function submit() {
    // SAID BESIDE THE FIELD, not as a schema message. Submitting with no yard chosen reached
    // the door and came back as "branchId: must be at least 1" — a sentence about a field
    // name the person never saw, under a heading saying some fields need attention.
    if (open.what === 'open-area' && !branchId) { setNeedYard(true); return; }
    setNeedYard(false);
    const ok = await runner.run((retryKey) => {
      if (open.what === 'open-area') {
        return api.command('yard.openArea', {
          branchId: Number(branchId), code: code.trim(), kind: kind as (typeof KINDS)[number],
          ...(name.trim() ? { name: name.trim() } : {}),
          ...(isolation ? { isDefaultQuarantine: true } : {}),
          openedOn: on,
        }, retryKey ? { idempotencyKey: retryKey } : {});
      }
      if (open.what === 'close-area') {
        return api.command('yard.closeArea', { areaId: open.area.areaId, closedOn: on, reason: reason.trim() });
      }
      if (open.what === 'nominate') {
        return api.command('yard.nominateQuarantineArea', { areaId: open.area.areaId, reason: reason.trim() });
      }
      return api.command('cylinder.moveToArea', {
        cylinderId: open.cylinderId, areaId: Number(areaId), reason: reason.trim(), on,
      });
    });
    if (ok) onDone();
  }

  const title =
    open.what === 'open-area' ? 'Open a yard area'
    : open.what === 'close-area' ? `Close ${open.area.code}`
    : open.what === 'nominate' ? `Isolate unsafe cylinders in ${open.area.code}`
    : `Where is ${open.label} standing?`;

  return (
    <Card title={title} sub={branchOf ? `At ${branchOf}.` : undefined}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}

      {open.what === 'open-area' ? (<>
        {yards.length > 1 ? (
          <Select name="yard-branch" label="Yard" value={branchId} onChange={setBranchId}
                  {...(needYard ? { error: 'Say which yard this area belongs to.' } : {})}
                  options={[{ value: '', label: 'Choose a yard' },
                            ...yards.map((y) => ({ value: String(y.branchId), label: `${y.code} — ${y.name}` }))]} />
        ) : null}
        <Text name="area-code" label="Code" value={code} onChange={setCode} placeholder="QUAR" required autoFocus />
        <Select name="area-kind" label="What it is for" value={kind} onChange={setKind}
                options={KINDS.map((k) => ({ value: k, label: KIND_LABEL[k]! }))} />
        <Text name="area-name" label="Name" value={name} onChange={setName} placeholder={KIND_LABEL[kind] ?? ''} />
        {kind === 'quarantine' ? (
          <label className="check">
            <input type="checkbox" name="area-isolation" checked={isolation}
                   onChange={(e: { target: { checked: boolean } }) => setIsolation(e.target.checked)} />
            {' '}This is where unsafe cylinders are isolated
          </label>
        ) : null}
        <Text name="area-opened" label="In use from" type="date" value={on} onChange={setOn} />
      </>) : null}

      {open.what === 'move' ? (
        <Select name="move-area" label="Moved to" value={areaId} onChange={setAreaId}
                options={[{ value: '', label: 'Choose an area' },
                          ...movable.map((a) => ({ value: String(a.areaId), label: `${a.code} — ${KIND_LABEL[a.kind] ?? a.kind}` }))]} />
      ) : null}

      {open.what !== 'open-area' ? (<>
        <TextArea name="area-reason" label="Why" value={reason} onChange={setReason} />
        {open.what !== 'nominate'
          ? <Text name="area-on" label={open.what === 'close-area' ? 'Closed on' : 'Moved on'}
                  type="date" value={on} onChange={setOn} />
          : null}
      </>) : null}

      <div className="row">
        <Button kind="primary" onClick={submit} disabled={runner.busy}>
          {open.what === 'open-area' ? 'Open it' : open.what === 'close-area' ? 'Close it'
            : open.what === 'nominate' ? 'Nominate it' : 'Record it'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}
